import React, { useState, useEffect } from "react";
import { getAllAdmins, createAdmin, updateAdmin, resetAdminPassword, deleteAdmin } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const AdminManagement = ({ adminId }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [language, setLanguage] = useState("en");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    full_name: "",
    phone: "",
    department: "",
    is_active: true,
  });

  const translations = {
    en: {
      title: "Admin Management",
      subtitle: "Manage Level 2 Admins (Level 1 Access Only)",
      loadingAdmins: "Loading admins...",
      addNewAdmin: "Add New Admin",
      cancel: "Cancel",
      createNewAdmin: "Create New Admin",
      editAdmin: "Edit Admin",
      id: "ID",
      username: "Username",
      fullName: "Full Name",
      email: "Email",
      phone: "Phone",
      department: "Department",
      active: "Active",
      actions: "Actions",
      edit: "Edit",
      delete: "Delete",
      resetPassword: "Reset Password",
      save: "Save",
      yes: "Yes",
      no: "No",
      passwordRequired: "Password is required for new admins",
      adminCreated: "Admin created successfully",
      adminUpdated: "Admin updated successfully",
      adminDeleted: "Admin deleted successfully",
      passwordReset: "Password reset successfully",
      deleteConfirm: "Are you sure you want to delete this admin?",
      noAdmins: "No level 2 admins found",
      formSection: "Admin Information",
      passwordSection: "Password",
      defaultPasswordPrompt: "Enter new password (min 12 chars, uppercase, lowercase, number, special char)",
    },
    vi: {
      title: "Quản Lý Quản Trị Viên",
      subtitle: "Quản Lý Quản Trị Viên Cấp 2 (Chỉ Cấp 1 Có Quyền)",
      loadingAdmins: "Đang tải danh sách...",
      addNewAdmin: "Thêm Quản Trị Viên Mới",
      cancel: "Hủy",
      createNewAdmin: "Tạo Quản Trị Viên Mới",
      editAdmin: "Chỉnh Sửa Quản Trị Viên",
      id: "ID",
      username: "Tên Đăng Nhập",
      fullName: "Họ và Tên",
      email: "Email",
      phone: "Số Điện Thoại",
      department: "Bộ Phận",
      active: "Hoạt Động",
      actions: "Hành Động",
      edit: "Chỉnh Sửa",
      delete: "Xóa",
      resetPassword: "Đặt Lại Mật Khẩu",
      save: "Lưu",
      yes: "Có",
      no: "Không",
      passwordRequired: "Mật khẩu là bắt buộc cho quản trị viên mới",
      adminCreated: "Quản trị viên được tạo thành công",
      adminUpdated: "Cập nhật quản trị viên thành công",
      adminDeleted: "Xóa quản trị viên thành công",
      passwordReset: "Đặt lại mật khẩu thành công",
      deleteConfirm: "Bạn có chắc muốn xóa quản trị viên này không?",
      noAdmins: "Chưa có quản trị viên cấp 2",
      formSection: "Thông Tin Quản Trị Viên",
      passwordSection: "Mật Khẩu",
      defaultPasswordPrompt: "Nhập mật khẩu mới (tối thiểu 12 ký tự, chữ hoa, chữ thường, số, ký tự đặc biệt)",
    },
  };

  const t = translations[language] || translations.en;

  const isActiveAdmin = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") return value.trim() === "1" || value.trim().toLowerCase() === "true";
    return false;
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      fetchAdmins();
    } else {
      setLoading(false);
      setToast({ message: "No authentication token found", type: "error" });
    }
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const data = await getAllAdmins();
      setAdmins(data.filter((admin) => admin.role === "admin_level_2" && isActiveAdmin(admin.is_active)));
    } catch (err) {
      setToast({ message: "Failed to load admins", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await updateAdmin(editingId, formData);
        setToast({ message: t.adminUpdated, type: "success" });
      } else {
        if (!formData.password) {
          setToast({ message: t.passwordRequired, type: "error" });
          return;
        }
        await createAdmin(formData);
        setToast({ message: t.adminCreated, type: "success" });
      }

      fetchAdmins();
      resetForm();
    } catch (err) {
      setToast({ message: err.message || "Failed to save admin", type: "error" });
    }
  };

  const handleEdit = (admin) => {
    setFormData({
      username: admin.username,
      password: "",
      email: admin.email,
      full_name: admin.full_name,
      phone: admin.phone || "",
      department: admin.department || "",
      is_active: admin.is_active,
    });
    setEditingId(admin.id);
    setShowForm(true);
  };

  const handleResetPassword = async (admin) => {
    const newPassword = prompt(t.defaultPasswordPrompt);
    if (!newPassword) return;

    try {
      await resetAdminPassword(admin.id, newPassword);
      setToast({ message: t.passwordReset, type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to reset password", type: "error" });
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(t.deleteConfirm)) return;

    try {
      await deleteAdmin(admin.id);
      setAdmins((prevAdmins) => prevAdmins.filter((item) => item.id !== admin.id));
      setToast({ message: t.adminDeleted, type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to delete admin", type: "error" });
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      email: "",
      full_name: "",
      phone: "",
      department: "",
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-manager-loading">{t.loadingAdmins}</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <div>
          <h1>{t.title}</h1>
          <p style={{ color: "#666", fontSize: "14px", marginTop: "5px" }}>{t.subtitle}</p>
        </div>
        <div>
          <button onClick={() => setLanguage("en")} style={{ marginRight: "10px", fontWeight: language === "en" ? "bold" : "normal" }}>
            English
          </button>
          <button onClick={() => setLanguage("vi")} style={{ fontWeight: language === "vi" ? "bold" : "normal" }}>
            Tiếng Việt
          </button>
        </div>
      </div>

      <div className="admin-manager-header" style={{ marginTop: "20px" }}>
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? t.cancel : t.addNewAdmin}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? t.editAdmin : t.createNewAdmin}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h4>{t.formSection}</h4>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange("username", e.target.value)}
                  placeholder="Username"
                  required
                  disabled={!!editingId}
                />
              </div>

              <div className="form-group">
                <label>{t.fullName} *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange("full_name", e.target.value)}
                  placeholder={t.fullName}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="email@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t.phone}</label>
                <input type="tel" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} placeholder="0123456789" />
              </div>

              <div className="form-group">
                <label>{t.department}</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleInputChange("department", e.target.value)}
                  placeholder={t.department}
                />
              </div>
            </div>

            {!editingId && (
              <div className="form-section">
                <h4>{t.passwordSection}</h4>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    placeholder={t.defaultPasswordPrompt}
                    required={!editingId}
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>{t.active}</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleInputChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {t.save} {editingId ? t.editAdmin : t.addNewAdmin}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                {t.cancel}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>{t.username}</th>
              <th>{t.fullName}</th>
              <th>{t.email}</th>
              <th>{t.phone}</th>
              <th>{t.department}</th>
              <th>{t.active}</th>
              <th>{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                  {t.noAdmins}
                </td>
              </tr>
            ) : (
              admins.map((admin, index) => (
                <tr key={admin.id}>
                  <td>{index + 1}</td>
                  <td>{admin.username}</td>
                  <td>{admin.full_name}</td>
                  <td>{admin.email}</td>
                  <td>{admin.phone || "N/A"}</td>
                  <td>{admin.department || "N/A"}</td>
                  <td>{admin.is_active ? t.yes : t.no}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(admin)}>
                      {t.edit}
                    </button>
                    <button className="btn-edit" onClick={() => handleResetPassword(admin)} style={{ backgroundColor: "#ff9800" }}>
                      {t.resetPassword}
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(admin)}>
                      {t.delete}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminManagement;
